// Users (platform admin) — who signed up: email, name, signup kind, active flag,
// created, and how many companies each person can reach. Row click opens user_detail.js
// (Identity/Companies/Practice/Subscriptions/Recent bugs/Support access).
import { html, signal, navigate } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { shell } from './shell.js';
import { pager, perPageSelect, DEFAULT_PER_PAGE } from '../shared/pager.js';
import { focusModal } from '../shared/modal.js';
import { icon } from '../shared/icons.js';
import { flashId, revealRow } from '../shared/listnav.js';
import { sortTh, nextSort, sortRows } from '../shared/sortcols.js';
import { isPartnerAdmin, isPlatformAdmin } from '../shared/auth.js';
const _regionalPartner = () => isPartnerAdmin() && !isPlatformAdmin();

// NOTE for the api owner: values are a guess pending confirmation — see the build report.
const SIGNUP_KINDS = [
  { value: 'business', label: 'Business' },
  { value: 'accountant', label: 'Accountant' },
];

// Filter chips (W9) — server-side via the list endpoint's new ?is_active / ?signup_kind params.
export const USER_STATUS_FILTERS = [
  { key: '', label: 'All' }, { key: 'active', label: 'Active' }, { key: 'inactive', label: 'Inactive' },
];
export const USER_KIND_FILTERS = [
  { key: '', label: 'All kinds' }, { key: 'business', label: 'Business' },
  { key: 'accountant', label: 'Accountant' },
];

// Build the list querystring from the current controls — one place so the KPI counts and the list
// use identical filter semantics (status 'active'|'inactive' → is_active true|false).
export function usersParams({ perPage, page, q, status, kind, locateId }){
  const p = new URLSearchParams({ per_page: String(perPage), page: String(page) });
  if(q && q.trim()) p.set('q', q.trim());
  if(status === 'active') p.set('is_active', 'true');
  else if(status === 'inactive') p.set('is_active', 'false');
  if(kind) p.set('signup_kind', kind);
  if(locateId) p.set('locate', String(locateId));
  return p;
}

// Value a sortable column reads off a row — kept client-side/current-page-only because
// GET /admin/users has no sort= param yet (see the build report's "Gaps" note).
function sortVal(row, key){
  if(key === 'name') return row.display_name || row.email || '';
  if(key === 'kind') return row.signup_kind || '';
  if(key === 'companies') return Number(row.company_count || 0);
  if(key === 'created') return row.created || '';
  return '';
}

export function usersScreen(){
  const rows = signal([]), total = signal(0), page = signal(1), pages = signal(1),
        perPage = signal(DEFAULT_PER_PAGE), loading = signal(true);
  const sort = signal({ key: '', dir: 'asc' });
  const statusFilter = signal(''), kindFilter = signal(''), stats = signal(null);
  let timer, lastQ = '', loadSeq = 0;   // loadSeq: drop a stale response that resolves after a newer one

  // add-user form state
  const formOpen = signal(false);
  const fEmail = signal(''), fName = signal(''), fPass = signal(''), fKind = signal('');
  const formErr = signal(''), saving = signal(false), invalid = signal({});
  // delete state — the user being deleted (null = modal closed), the typed "DELETE" confirmation,
  // the in-flight flag, and the block reason (e.g. 409) shown inside the modal.
  const delUser = signal(null), delText = signal(''), deleting = signal(false), delErr = signal('');

  async function load(locateId){
    loading.value = true;
    const seq = ++loadSeq;
    const p = usersParams({ perPage: perPage.value, page: page.value, q: lastQ,
                            status: statusFilter.value, kind: kindFilter.value, locateId });
    try {
      const r = await api.get('/admin/users?' + p);
      if(seq !== loadSeq) return;                 // superseded by a newer load — discard
      rows.value = r.data || []; total.value = r.total || 0; pages.value = r.total_pages || 1;
      if(locateId && r.locate_page) page.value = r.locate_page;
    } catch {
      if(seq !== loadSeq) return;
      rows.value = []; total.value = 0; pages.value = 1;
    }
    if(seq === loadSeq) loading.value = false;
  }
  // KPI band — platform-wide counts via the same list endpoint (per_page=1 → total). Fired once on
  // mount; a filter chip narrows the LIST, not the cards.
  async function loadStats(){
    const count = qs => api.get('/admin/users?per_page=1' + (qs ? '&' + qs : ''))
      .then(r => (r && r.total) || 0).catch(() => null);
    const [all, active, inactive, business, accountant] = await Promise.all([
      count(''), count('is_active=true'), count('is_active=false'),
      count('signup_kind=business'), count('signup_kind=accountant'),
    ]);
    stats.value = { all, active, inactive, business, accountant };
  }
  load(); loadStats();
  function onSearch(e){ lastQ = e.target.value; page.value = 1; clearTimeout(timer); timer = setTimeout(load, 220); }
  function onPer(e){ perPage.value = e.target.value; page.value = 1; load(); }
  function goPage(p){ page.value = p; load(); }
  function onSort(key){ sort.value = nextSort(sort.value, key, key === 'created' ? 'desc' : 'asc'); }
  function setStatus(k){ statusFilter.value = k; page.value = 1; load(); }
  function setKind(k){ kindFilter.value = k; page.value = 1; load(); }

  // delete a user — a type-to-confirm modal: are-you-sure + the admin must type DELETE (uppercase)
  // before the Delete button enables. The trash button on the row opens it (stops propagation so it
  // never triggers the row's navigate). A block reason (e.g. 409) shows inside the modal.
  const delConfirmed = () => delText.value.trim() === 'DELETE';
  function askDelete(u){ delUser.value = u; delText.value = ''; delErr.value = ''; focusModal(); }
  function closeDelete(){ delUser.value = null; delText.value = ''; delErr.value = ''; }
  async function confirmDelete(){
    if(!delUser.value || !delConfirmed()) return;   // never fire without the typed confirmation
    deleting.value = true; delErr.value = '';
    try {
      await api.del('/admin/users/' + delUser.value.id);
      closeDelete();
      await load(); loadStats();                 // refresh the list + the KPI counts
    } catch(err){
      delErr.value = (err.data && err.data.error) || err.message || 'Delete failed';
    }
    deleting.value = false;
  }

  const clearInvalid = k => { if(invalid.value[k]) invalid.value = { ...invalid.value, [k]: false }; };
  function openCreate(){
    fEmail.value = ''; fName.value = ''; fPass.value = ''; fKind.value = '';
    formErr.value = ''; invalid.value = {}; formOpen.value = true; focusModal();
  }
  function closeForm(){ formOpen.value = false; }

  async function save(e){
    e.preventDefault();
    const email = fEmail.value.trim();
    const inv = {};
    if(!email || !email.includes('@')) inv.email = true;
    if(!fName.value.trim()) inv.name = true;
    if(!fPass.value) inv.pass = true;
    invalid.value = inv;
    if(Object.keys(inv).length){ formErr.value = 'Please fill in the highlighted fields.'; return; }
    saving.value = true; formErr.value = '';
    const body = { email, display_name: fName.value.trim(), temp_password: fPass.value,
                   signup_kind: fKind.value || null };
    try {
      const res = await api.post('/admin/users', body);
      formOpen.value = false;
      await revealRow(load, res && res.id);
      loadStats();                                 // refresh the KPI band (matches confirmDelete)
    } catch(err){
      formErr.value = err.status === 409
        ? 'A user with this email already exists'
        : (err.data && err.data.error) || err.message || 'Save failed';
    }
    saving.value = false;
  }

  // Single reactive block: rows + pager together (the pager-inside-list-block house pattern —
  // see CLAUDE.md). Sort is client-side over the CURRENT page only (no sort= param on the list
  // endpoint yet), so every signal it needs is read at the top and passed to sortRows as a plain
  // value.
  const list = () => {
    if(loading.value) return html`<div class="muted-block">Loading…</div>`;
    if(!rows.value.length) return html`<div class="muted-block">No users match.</div>`;
    const cur = sort.value, hl = flashId.value;
    const sorted = sortRows(rows.value, cur, sortVal);
    const pg = () => pager({ page: page.value, totalPages: pages.value, total: total.value,
                             perPage: Number(perPage.value), onPage: goPage });
    return html`<div class="list-wrap">
      ${pg()}
      <div class="table-wrap"><table class="cb-table user-table">
        <thead><tr>
          ${sortTh('User', 'name', cur, onSort)}
          ${sortTh('Kind', 'kind', cur, onSort)}
          ${sortTh('Companies', 'companies', cur, onSort, { align: 'right' })}
          ${sortTh('Joined', 'created', cur, onSort)}
          <th>Status</th>
          <th></th>
          <th></th>
        </tr></thead>
        <tbody>${sorted.map(u => html`
          <tr class=${'user-row' + (hl === u.id ? ' row-hl' : '')} @click=${() => navigate('/users/' + u.id)}>
            <td><b>${u.display_name || u.email}</b><div class="row-sub">${u.email}</div></td>
            <td>${u.signup_kind || '—'}</td>
            <td class="text-right">${u.company_count || 0}</td>
            <td class="mono">${u.created || '—'}</td>
            <td>${u.is_active ? html`<span class="tag-on">active</span>` : html`<span class="tag-off">inactive</span>`}</td>
            <td class="ta-r nowrap user-del-cell"><button type="button" class="btn-row-del"
              title="Delete user" aria-label="Delete user"
              @click=${e => { e.stopPropagation(); askDelete(u); }}><span class="del-ico" aria-hidden="true">${icon('trash-2', {size: 14})}</span></button></td>
            <td class="row-chevron">${icon('chevron-right', {size: 14})}</td>
          </tr>`)}</tbody>
      </table></div>
      <div class="form-note user-sort-note">Sorting applies to the current page only.</div>
      ${pg()}
    </div>`;
  };

  const kpiBand = () => {
    const s = stats.value;
    const fmt = v => (v === null || v === undefined) ? '—' : String(v);
    const cards = [
      { label: 'People',     val: s ? fmt(s.all) : '…',        sub: 'signed up',    accent: 'primary' },
      { label: 'Active',     val: s ? fmt(s.active) : '…',     sub: 'can sign in',  accent: 'success' },
      { label: 'Inactive',   val: s ? fmt(s.inactive) : '…',   sub: 'disabled',     accent: 'warning' },
      { label: 'Business',   val: s ? fmt(s.business) : '…',   sub: 'own books',    accent: 'info' },
      { label: 'Accountant', val: s ? fmt(s.accountant) : '…', sub: 'practices',    accent: 'dark' },
    ];
    return html`<div class="kpi-grid">${cards.map(c => html`
      <div class=${'kpi accent-' + c.accent}>
        <div class="card-body">
          <div class="kpi-label">${c.label}</div>
          <div class="kpi-val">${c.val}</div>
          <div class="kpi-sub">${c.sub}</div>
        </div>
      </div>`)}</div>`;
  };

  const content = html`<div class="page">
    <div class="page-head with-action">
      <div><h1 class="page-title">Users</h1>
        <div class="page-sub">${() => total.value + (total.value === 1 ? ' person' : ' people')} shown</div></div>
      <button class="btn btn-primary" ?hidden=${_regionalPartner()} @click=${openCreate}>+ Add user</button>
    </div>
    ${kpiBand}
    <div class="toolbar">
      <input class="form-control" type="search" placeholder="Search… e.g. callan or @codeinfinity"
        @input=${onSearch}>
      ${perPageSelect(perPage.value, onPer)}
    </div>
    <div class="adm-chips">
      ${() => USER_STATUS_FILTERS.map(f => html`<button type="button"
        class=${'btn btn-sm ' + (statusFilter.value === f.key ? 'btn-primary' : 'btn-outline')}
        @click=${() => setStatus(f.key)}>${f.label}</button>`)}
      <span class="adm-chip-gap"></span>
      ${() => USER_KIND_FILTERS.map(f => html`<button type="button"
        class=${'btn btn-sm ' + (kindFilter.value === f.key ? 'btn-primary' : 'btn-outline')}
        @click=${() => setKind(f.key)}>${f.label}</button>`)}
    </div>
    ${list}

    <div class="modal-overlay" ?hidden=${() => !formOpen.value}>
      <form class="modal-card" @submit=${save}>
        <div class="modal-head"><h3>Add user</h3><button type="button" class="modal-x" @click=${closeForm}>✕</button></div>
        <label class="form-label">Email</label>
        <input class=${() => 'form-control mb-2' + (invalid.value.email ? ' is-invalid' : '')}
               type="email" .value=${fEmail} @input=${e => { fEmail.value = e.target.value; clearInvalid('email'); }}
               placeholder="e.g. jane@company.co.za">
        <label class="form-label">Display name</label>
        <input class=${() => 'form-control mb-2' + (invalid.value.name ? ' is-invalid' : '')}
               .value=${fName} @input=${e => { fName.value = e.target.value; clearInvalid('name'); }}
               placeholder="e.g. Jane Mokoena">
        <label class="form-label">Temporary password</label>
        <input class=${() => 'form-control mb-1' + (invalid.value.pass ? ' is-invalid' : '')}
               type="password" .value=${fPass} @input=${e => { fPass.value = e.target.value; clearInvalid('pass'); }}
               placeholder="e.g. a random starter password">
        <div class="form-note">They must change it at first sign-in.</div>
        <label class="form-label">Signup kind</label>
        <select class="form-control mb-2" .value=${fKind} @change=${e => { fKind.value = e.target.value; }}>
          <option value="">— Select —</option>
          ${SIGNUP_KINDS.map(k => html`<option value=${k.value}>${k.label}</option>`)}
        </select>
        ${() => formErr.value ? html`<div class="alert alert-danger py-2 mb-2">${formErr.value}</div>` : null}
        <div class="form-actions">
          <button type="button" class="btn btn-outline" @click=${closeForm}>Cancel</button>
          <button type="submit" class="btn btn-primary" ?disabled=${() => saving.value}>${() => saving.value ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </div>

    <div class="modal-overlay del-user-modal" ?hidden=${() => !delUser.value}>
      <div class="modal-card">
        <div class="modal-head"><h3>Delete user?</h3><button type="button" class="modal-x" @click=${closeDelete}>✕</button></div>
        <p class="form-note">You're about to permanently delete
          <b>${() => delUser.value ? (delUser.value.display_name || delUser.value.email) : ''}</b>.
          This removes their sign-in and tears down the demo company they own. This can't be undone.</p>
        <label class="form-label">Type <b>DELETE</b> to confirm</label>
        <input class="form-control mb-2" autocomplete="off" spellcheck="false" placeholder="DELETE"
               .value=${delText} @input=${e => { delText.value = e.target.value; }}>
        ${() => delErr.value ? html`<div class="alert alert-danger py-2 mb-2">${delErr.value}</div>` : null}
        <div class="form-actions">
          <button type="button" class="btn btn-outline" @click=${closeDelete}>Cancel</button>
          <button type="button" class="btn btn-danger" ?disabled=${() => deleting.value || !delConfirmed()}
                  @click=${confirmDelete}>${() => deleting.value ? 'Deleting…' : 'Delete user'}</button>
        </div>
      </div>
    </div>
  </div>`;
  return shell('users', 'Users', content);
}
